import { createClient, type Session, type SupabaseClient } from "@supabase/supabase-js";
import { isSupabaseConfigured, readPublicConfig } from "../config";

export type AdminLocation = {
  id: string;
  slug: string;
  name: string;
  mode: "online" | "in_person";
  description?: string | null;
  addressLine?: string | null;
  postalCode?: string | null;
  city?: string | null;
  countryCode?: string | null;
  timezone: string;
  videoProvider?: string | null;
  sortOrder: number;
  isActive: boolean;
  travelBufferMinutes: number;
};

export type AdminOffer = {
  id: string;
  slug: string;
  title: string;
  isActive: boolean;
};

export type AdminOfferLocation = {
  offerId: string;
  locationId: string;
  isActive: boolean;
};

export type AdminResource = {
  id: string;
  name: string;
  timezone: string;
  isActive: boolean;
};

export type AdminAvailabilityWindow = {
  id: string;
  bookingLocationId: string;
  resourceId: string;
  startsAt: string;
  endsAt: string;
  timezone: string;
  slotIntervalMinutes: number;
  bufferAfterMinutes: number;
  isActive: boolean;
  openSlotCount: number;
};

export type AdminChange = {
  id: string;
  actorType: "human" | "assistant" | "system";
  actor: string;
  action: string;
  entityType: string;
  entityId?: string | null;
  details: Record<string, unknown>;
  createdAt: string;
};

export type AdminSnapshot = {
  source: "live";
  adminEmail: string;
  locations: AdminLocation[];
  offers: AdminOffer[];
  offerLocations: AdminOfferLocation[];
  resources: AdminResource[];
  windows: AdminAvailabilityWindow[];
  recentChanges: AdminChange[];
};

export type AdminPasswordSetupChallenge = {
  challengeId: string;
  expiresInSeconds: number;
};

export type SaveLocationInput = Omit<AdminLocation, "id" | "slug"> & { id?: string };

let client: SupabaseClient | undefined;

export function getAdminSupabaseClient(): SupabaseClient {
  if (client) return client;

  const config = readPublicConfig(import.meta.env);
  const { supabaseUrl, supabasePublishableKey } = config;
  if (!isSupabaseConfigured(config) || !supabaseUrl || !supabasePublishableKey) {
    throw new Error("Die geschützte Verwaltung ist noch nicht vollständig konfiguriert.");
  }

  client = createClient(supabaseUrl, supabasePublishableKey, {
    auth: {
      persistSession: true,
      autoRefreshToken: true,
      detectSessionInUrl: true,
      flowType: "pkce",
      experimental: { passkey: true },
    },
  });

  return client;
}

export async function signInAdminWithPasskey(): Promise<void> {
  const { error } = await getAdminSupabaseClient().auth.signInWithPasskey();
  if (error) throw new Error(passkeyError(error.message));
}

export async function registerAdminPasskey(): Promise<void> {
  const supabase = getAdminSupabaseClient();
  const { error } = await supabase.auth.registerPasskey();
  if (error) throw new Error(passkeyError(error.message));

  const { data: currentUser } = await supabase.auth.getUser();
  const { error: metadataError } = await supabase.auth.updateUser({
    data: {
      ...(currentUser.user?.user_metadata ?? {}),
      admin_passkey_enabled: true,
    },
  });
  if (metadataError) throw new Error(authError(metadataError.message));
}

export async function signInAdminWithPassword(email: string, password: string): Promise<void> {
  const { error } = await getAdminSupabaseClient().auth.signInWithPassword({
    email: email.trim().toLowerCase(),
    password,
  });

  if (error) throw new Error(authError(error.message));
}

export async function updateAdminPassword(password: string): Promise<void> {
  const supabase = getAdminSupabaseClient();
  const { data: current } = await supabase.auth.getUser();
  const { error } = await supabase.auth.updateUser({
    password,
    data: {
      ...(current.user?.user_metadata ?? {}),
      admin_password_enabled: true,
    },
  });

  if (error) throw new Error(authError(error.message));
}

export async function requestAdminPasswordSetupCode(
  email: string,
): Promise<AdminPasswordSetupChallenge> {
  return adminAccessRequest<AdminPasswordSetupChallenge>({
    action: "request_code",
    email: email.trim().toLowerCase(),
  });
}

/** @deprecated The visible administration now uses a verification code, not a link. */
export async function requestAdminMagicLink(email: string): Promise<void> {
  await requestAdminPasswordSetupCode(email);
}

export async function completeAdminPasswordSetup(input: {
  email: string;
  challengeId: string;
  code: string;
  password: string;
}): Promise<void> {
  await adminAccessRequest({
    action: "set_password",
    email: input.email.trim().toLowerCase(),
    challengeId: input.challengeId,
    code: input.code.trim(),
    password: input.password,
  });
}

export async function completeAdminCodeSignIn(input: {
  email: string;
  challengeId: string;
  code: string;
}): Promise<void> {
  const internalPassword = createInternalAdminPassword();

  await completeAdminPasswordSetup({
    email: input.email,
    challengeId: input.challengeId,
    code: input.code,
    password: internalPassword,
  });

  await signInAdminWithPassword(input.email, internalPassword);
}

function createInternalAdminPassword(): string {
  const alphabet =
    "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789-_.!@#$%";
  const bytes = new Uint8Array(40);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (value) => alphabet[value % alphabet.length]).join("");
}

export async function readAdminSession(): Promise<Session | null> {
  const { data, error } = await getAdminSupabaseClient().auth.getSession();
  if (error) throw new Error(authError(error.message));
  return data.session;
}

export async function signOutAdmin(): Promise<void> {
  const { error } = await getAdminSupabaseClient().auth.signOut();
  if (error) throw new Error(authError(error.message));
}

export async function loadAdminSnapshot(accessToken: string): Promise<AdminSnapshot> {
  return adminRequest<AdminSnapshot>(accessToken, { method: "GET" });
}

export async function saveAdminLocation(
  accessToken: string,
  location: SaveLocationInput,
): Promise<void> {
  await adminRequest(accessToken, {
    method: "POST",
    body: JSON.stringify({ action: "save_location", location }),
  });
}

export async function setAdminOfferLocation(
  accessToken: string,
  offerId: string,
  locationId: string,
  isActive: boolean,
): Promise<void> {
  await adminRequest(accessToken, {
    method: "POST",
    body: JSON.stringify({ action: "set_offer_location", offerId, locationId, isActive }),
  });
}

export async function createAdminAvailabilityWindow(
  accessToken: string,
  input: {
    bookingLocationId: string;
    resourceId: string;
    startsAt: string;
    endsAt: string;
    timezone: string;
    slotIntervalMinutes: number;
    bufferAfterMinutes: number;
  },
): Promise<{ insertedSlots?: number; windowId?: string }> {
  return adminRequest(accessToken, {
    method: "POST",
    body: JSON.stringify({ action: "create_availability_window", ...input }),
  });
}

export async function deactivateAdminAvailabilityWindow(
  accessToken: string,
  windowId: string,
): Promise<void> {
  await adminRequest(accessToken, {
    method: "POST",
    body: JSON.stringify({ action: "deactivate_availability_window", windowId }),
  });
}

async function adminAccessRequest<T = unknown>(body: Record<string, unknown>): Promise<T> {
  const config = readPublicConfig(import.meta.env);
  const response = await fetch(config.bookingAdminAccessApiUrl, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });

  const payload = (await readJson(response)) as T & { error?: string };
  if (!response.ok) throw new Error(adminAccessError(payload.error));
  return payload;
}

async function adminRequest<T = unknown>(accessToken: string, init: RequestInit): Promise<T> {
  const config = readPublicConfig(import.meta.env);
  const response = await fetch(config.bookingAdminApiUrl, {
    ...init,
    headers: {
      Authorization: `Bearer ${accessToken}`,
      "Content-Type": "application/json",
      ...init.headers,
    },
  });

  const payload = (await readJson(response)) as T & { error?: string };
  if (!response.ok) throw new Error(adminError(payload.error));
  return payload;
}

async function readJson(response: Response): Promise<unknown> {
  try {
    return await response.json();
  } catch {
    return {};
  }
}

function passkeyError(error: string): string {
  const normalized = error.toLowerCase();

  if (normalized.includes("not supported")) {
    return "Dieses Gerät oder dieser Browser unterstützt Passkeys hier nicht.";
  }

  if (normalized.includes("cancel") || normalized.includes("notallowed")) {
    return "Die Passkey-Anmeldung wurde abgebrochen.";
  }

  if (
    normalized.includes("credential") ||
    normalized.includes("passkey") ||
    normalized.includes("webauthn")
  ) {
    return "Es wurde kein passender Passkey gefunden oder die Anmeldung konnte nicht bestätigt werden.";
  }

  return error;
}

function authError(error: string): string {
  const normalized = error.toLowerCase();

  if (normalized.includes("invalid login credentials")) {
    return "Das Passwort ist nicht richtig oder wurde noch nicht eingerichtet.";
  }

  if (normalized.includes("password should be at least")) {
    return "Das Passwort ist zu kurz.";
  }

  return error;
}

function adminAccessError(error?: string): string {
  switch (error) {
    case "invalid_email":
    case "admin_access_denied":
      return "Diese E-Mail-Adresse hat keinen Zugriff auf die Booking-Verwaltung.";
    case "code_rate_limited":
      return "Es wurde gerade bereits ein Code verschickt. Bitte warte kurz und verwende die neueste E-Mail.";
    case "invalid_or_expired_code":
      return "Der Code ist falsch oder nicht mehr gültig. Bitte fordere einen neuen Code an.";
    case "password_too_short":
      return "Das Passwort muss mindestens zwölf Zeichen lang sein.";
    case "password_too_long":
      return "Das Passwort ist zu lang.";
    case "code_email_failed":
      return "Der Code konnte gerade nicht per E-Mail verschickt werden. Bitte versuche es später erneut.";
    case "admin_user_not_found":
      return "Das Verwaltungskonto wurde nicht gefunden.";
    default:
      return "Die Anmeldung mit E-Mail-Code konnte gerade nicht abgeschlossen werden.";
  }
}

function adminError(error?: string): string {
  switch (error) {
    case "authentication_required":
      return "Die Anmeldung ist abgelaufen. Bitte erneut anmelden.";
    case "admin_access_denied":
      return "Diese E-Mail-Adresse hat keinen Zugriff auf die Booking-Verwaltung.";
    case "location_name_required":
      return "Bitte einen Namen für den Ort eintragen.";
    case "invalid_location_mode":
      return "Bitte Online oder Präsenz als Format auswählen.";
    case "location_timezone_required":
    case "invalid_location_timezone":
      return "Bitte eine gültige Zeitzone auswählen.";
    case "invalid_travel_buffer":
      return "Die Reisezeit muss zwischen 0 und 360 Minuten liegen.";
    case "availability_window_in_past":
      return "Das Zeitfenster darf nicht in der Vergangenheit beginnen.";
    case "availability_window_too_large":
      return "Ein einzelnes Zeitfenster darf höchstens 31 Tage umfassen.";
    case "location_unavailable":
      return "Der ausgewählte Ort ist nicht aktiv.";
    case "resource_unavailable":
      return "Die ausgewählte Person oder Ressource ist nicht aktiv.";
    case "availability_window_not_found":
      return "Das Zeitfenster wurde nicht gefunden.";
    case "no_active_offers_for_location":
      return "Für diesen Ort ist noch kein Angebot aktiviert.";
    default:
      return "Die Änderung konnte gerade nicht gespeichert werden.";
  }
}
